import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { makeScheduleAsActive } from "services/schedule-versions-services";
import { message } from "antd";
import {
  TActivityHookInfo,
  TEnrichedActivity,
  TFeatureActivityWithInstance
} from "../models";
import {
  FEATURE_ACTIVITIES,
  OPEN_MATERIAL_DELTAS_QUERY,
  QUERY_SCHEDULE_VERSIONS
} from "../queries/queries";
import useActivityFiltersMetadata, {
  buildFilterOptionsFromMetadata,
  RECOMMENDER_JOB_POLL_INTERVAL_MS
} from "./use-activity-filters";

type TScheduleVersion = {
  id: number;
  import_log_id: string;
  is_active: boolean;
  schedule_name: string;
  schedule_date: string;
  schedule_type: string;
  activated_at: null;
  created_at: string;
  import_log: {
    id: string;
    resolved_values_s3_prefix: string;
    source_values_s3_prefix: string;
    created_at: string;
    created_by_user: {
      id: string;
      first_name: string;
      last_name: string;
    };
  };
};

const getScheduleFileExtension = (
  selectedSchedule: TScheduleVersion,
  fileExtention: string
) => {
  if (!selectedSchedule?.import_log?.source_values_s3_prefix) return false;
  const fileExt = selectedSchedule?.import_log?.source_values_s3_prefix
    .split(".")
    .pop();
  if (fileExtention === fileExt) return true;
  return false;
};

type UseActivityListHookOptions = {
  preferredScheduleId?: string;
  uploadedScheduleName?: string;
  uploadedScheduleDate?: string;
  selectedEntityIds?: string[];
};

type TMatchDeltaJob = {
  id: string;
  status: string;
  config?: {
    in_progress_material_feature_instance_ids?: unknown;
  } | null;
};

const collectInProgressMaterialIds = (
  jobs: TMatchDeltaJob[] | undefined
): Set<string> => {
  const ids = new Set<string>();
  jobs?.forEach((job) => {
    const list = job.config?.in_progress_material_feature_instance_ids;
    if (!Array.isArray(list)) return;
    list.forEach((id) => {
      if (typeof id === "string" && id) ids.add(id);
    });
  });
  return ids;
};

const isSameDate = (left?: string, right?: string) => {
  if (!left || !right) return false;
  const leftDate = new Date(left);
  const rightDate = new Date(right);
  if (Number.isNaN(leftDate.getTime()) || Number.isNaN(rightDate.getTime())) {
    return false;
  }
  return leftDate.toDateString() === rightDate.toDateString();
};

export default function useActivityListHook(
  optionsOrPreferredScheduleId: UseActivityListHookOptions | string = {}
) {
  const options =
    typeof optionsOrPreferredScheduleId === "string"
      ? { preferredScheduleId: optionsOrPreferredScheduleId }
      : optionsOrPreferredScheduleId;
  const {
    preferredScheduleId,
    uploadedScheduleName,
    uploadedScheduleDate,
    selectedEntityIds = []
  } = options;
  const hasImportPrefillHints = Boolean(
    preferredScheduleId || uploadedScheduleName || uploadedScheduleDate
  );
  const [loading, setLoading] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>();
  const [selectedScheduleInfo, setSelectedScheduleInfo] =
    useState<TScheduleVersion>();
  const [hasActive, setHasActive] = useState(true);
  const hasAutoActivatedPreferredSchedule = useRef(false);

  const { gqlClientForProject, tokenRetrievalState, isFeatureFlagEnabled } =
    useContext(ProjectContext);

  const isAdvanceFilterEnabled = Boolean(
    isFeatureFlagEnabled?.("SCHEDULE_ADVANCE_FILTER_V1") ||
    localStorage.getItem("SCHEDULE_ADVANCE_FILTER_V1") === "true"
  );
  const isSmartSetupEnabled = Boolean(isFeatureFlagEnabled?.("SMART_LINKING"));

  const { data: featureActivitiesList, refetch: refetchActivityList } =
    useCIQQuery<{
      feature_activities: TFeatureActivityWithInstance[];
    }>(FEATURE_ACTIVITIES, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const { data: scheduleVersions, refetch: refetchScheduleVersions } =
    useCIQQuery<{
      schedule_import_details: TScheduleVersion[];
    }>(QUERY_SCHEDULE_VERSIONS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const xerS3Key = useMemo(() => {
    if (!selectedScheduleId) return undefined;
    const selectedSchedule = scheduleVersions?.schedule_import_details?.find(
      (schedule) => schedule.import_log_id === selectedScheduleId
    );
    const sourceKey =
      selectedSchedule?.import_log?.source_values_s3_prefix?.trim();
    return sourceKey || undefined;
  }, [scheduleVersions?.schedule_import_details, selectedScheduleId]);

  const {
    metadataMap,
    hasCompletedRecommenderJob,
    isRecommenderJobPending,
    hasRecommenderJobError,
    recommenderJobId
  } = useActivityFiltersMetadata(
    isAdvanceFilterEnabled || isSmartSetupEnabled,
    xerS3Key
  );

  const shouldFetchMaterialDeltas = Boolean(
    gqlClientForProject && selectedEntityIds.length > 0
  );
  const {
    data: materialDeltaData,
    startPolling: startMaterialDeltaPolling,
    stopPolling: stopMaterialDeltaPolling
  } = useCIQQuery<{
    link_recommender_job: TMatchDeltaJob[];
  }>(OPEN_MATERIAL_DELTAS_QUERY, {
    client: gqlClientForProject,
    skip: !shouldFetchMaterialDeltas
  });

  const inProgressMaterialIds = useMemo(
    () => collectInProgressMaterialIds(materialDeltaData?.link_recommender_job),
    [materialDeltaData?.link_recommender_job]
  );
  const hasOpenMaterialDeltas = Boolean(
    materialDeltaData?.link_recommender_job?.length
  );

  useEffect(() => {
    if (!shouldFetchMaterialDeltas || !hasOpenMaterialDeltas) {
      stopMaterialDeltaPolling?.();
      return undefined;
    }

    startMaterialDeltaPolling?.(RECOMMENDER_JOB_POLL_INTERVAL_MS);
    return () => {
      stopMaterialDeltaPolling?.();
    };
  }, [
    hasOpenMaterialDeltas,
    shouldFetchMaterialDeltas,
    startMaterialDeltaPolling,
    stopMaterialDeltaPolling
  ]);

  const allActivities = useMemo(
    () => featureActivitiesList?.feature_activities || [],
    [featureActivitiesList?.feature_activities]
  );

  const isCSVFile = useMemo(() => {
    return selectedScheduleInfo
      ? getScheduleFileExtension(selectedScheduleInfo, "csv")
      : false;
  }, [selectedScheduleInfo]);

  const activities = useMemo((): TEnrichedActivity[] => {
    const scheduleActivities = !selectedScheduleId
      ? allActivities
      : allActivities.filter(
          (activity) => activity.import_log_id === selectedScheduleId
        );

    if (!isAdvanceFilterEnabled || metadataMap.size === 0) {
      return scheduleActivities;
    }

    return scheduleActivities.map((activity) => {
      const meta =
        metadataMap.get(activity.feature_instance_id) ||
        metadataMap.get(activity.feature_instance?.number);
      if (!meta) return activity;
      return {
        ...activity,
        phase: meta.phase,
        csi_division: meta.csi_division,
        activity_type: meta.activity_type,
        location_tags: meta.location_tags
      };
    });
  }, [allActivities, selectedScheduleId, isAdvanceFilterEnabled, metadataMap]);

  const filterOptions = useMemo(
    () =>
      isAdvanceFilterEnabled
        ? buildFilterOptionsFromMetadata(metadataMap)
        : {
            phases: [],
            activityTypes: [],
            csiDivisions: [],
            locations: []
          },
    [isAdvanceFilterEnabled, metadataMap]
  );

  const createActiveSchedule = useCallback(
    (schedule_Id: string) => {
      const previousScheduleId = selectedScheduleId;
      // Optimistically switch rows in the grid without waiting for refetch.
      setSelectedScheduleId(schedule_Id);
      setLoading(true);
      makeScheduleAsActive(schedule_Id, tokenRetrievalState?.token)
        .then((res: any) => {
          if (res.error) {
            message.error(res.error?.message || "Something went wrong");
            if (previousScheduleId) {
              setSelectedScheduleId(previousScheduleId);
            }
          } else {
            refetchActivityList();
            refetchScheduleVersions();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      refetchActivityList,
      refetchScheduleVersions,
      selectedScheduleId,
      tokenRetrievalState?.token
    ]
  );

  useEffect(() => {
    if (featureActivitiesList?.feature_activities) {
      if (selectedScheduleId) {
        return;
      }
      if (preferredScheduleId) {
        setSelectedScheduleId(preferredScheduleId);
        return;
      }
      if (uploadedScheduleName || uploadedScheduleDate) {
        return;
      }
      if (featureActivitiesList.feature_activities.length) {
        setSelectedScheduleId(
          featureActivitiesList.feature_activities[0].import_log_id
        );
      } else if (scheduleVersions?.schedule_import_details?.length) {
        setSelectedScheduleId(
          scheduleVersions?.schedule_import_details[0].import_log_id
        );
        createActiveSchedule(
          scheduleVersions?.schedule_import_details[0].import_log_id
        );
      }
    }
  }, [
    createActiveSchedule,
    featureActivitiesList,
    preferredScheduleId,
    selectedScheduleId,
    scheduleVersions?.schedule_import_details,
    uploadedScheduleDate,
    uploadedScheduleName
  ]);

  useEffect(() => {
    if (scheduleVersions?.schedule_import_details?.length) {
      const hasActiveTemp = scheduleVersions?.schedule_import_details.some(
        (e) => e.is_active
      );
      setHasActive(hasActiveTemp);
      const preferredById = preferredScheduleId
        ? scheduleVersions.schedule_import_details.find(
            (e) => e.import_log_id === preferredScheduleId
          )
        : undefined;
      const preferredByMeta = uploadedScheduleName
        ? scheduleVersions.schedule_import_details.find(
            (e) =>
              e.schedule_name === uploadedScheduleName &&
              isSameDate(e.schedule_date, uploadedScheduleDate)
          )
        : undefined;
      const preferredSchedule = preferredById || preferredByMeta;
      if (
        preferredSchedule &&
        selectedScheduleId !== preferredSchedule.import_log_id
      ) {
        setSelectedScheduleId(preferredSchedule.import_log_id);
      }
      const selectedScheduleObj =
        scheduleVersions?.schedule_import_details?.find(
          (e: any) => e.import_log_id === selectedScheduleId
        );
      setSelectedScheduleInfo(selectedScheduleObj);
    }
  }, [
    scheduleVersions,
    selectedScheduleId,
    preferredScheduleId,
    uploadedScheduleDate,
    uploadedScheduleName
  ]);

  useEffect(() => {
    if (
      !hasImportPrefillHints ||
      hasActive ||
      !scheduleVersions?.schedule_import_details?.length ||
      hasAutoActivatedPreferredSchedule.current
    ) {
      return;
    }

    const preferredSchedule =
      (preferredScheduleId
        ? scheduleVersions.schedule_import_details.find(
            (schedule) => schedule.import_log_id === preferredScheduleId
          )
        : undefined) ||
      scheduleVersions.schedule_import_details.find(
        (schedule) =>
          schedule.schedule_name === uploadedScheduleName &&
          isSameDate(schedule.schedule_date, uploadedScheduleDate)
      ) ||
      scheduleVersions.schedule_import_details[0];
    if (preferredSchedule) {
      hasAutoActivatedPreferredSchedule.current = true;
      setSelectedScheduleId(preferredSchedule.import_log_id);
      createActiveSchedule(preferredSchedule.import_log_id);
    }
  }, [
    createActiveSchedule,
    hasActive,
    hasImportPrefillHints,
    preferredScheduleId,
    scheduleVersions?.schedule_import_details,
    uploadedScheduleDate,
    uploadedScheduleName
  ]);

  return {
    activities,
    isCSVFile,
    hasActive,
    loading,
    createActiveSchedule,
    selectedScheduleId,
    refetchActivityList,
    refetchScheduleVersions,
    filterOptions,
    hasCompletedRecommenderJob,
    isRecommenderJobPending,
    hasRecommenderJobError,
    inProgressMaterialIds,
    recommenderJobId,
    scheduleVersionsOptions:
      scheduleVersions?.schedule_import_details?.map((x) => ({
        value: x.import_log_id,
        label: `${x?.schedule_name}_${x?.schedule_date}`
      })) || []
  } as TActivityHookInfo;
}
